'use client'

import { useEffect, type RefObject } from 'react'

/** Where a page's own sticky bar ends, in px from the viewport top, once stuck. */
export const STICKY_BOTTOM_VAR = '--tls-sticky-bottom'

/**
 * Publish how far down the viewport a sticky bar reaches while stuck, so the
 * document's scroll-padding-top (app/globals.css) keeps focused and
 * anchor-scrolled content below it instead of underneath it (WCAG 2.2 SC 2.4.11).
 *
 * The fixed nav is reserved in CSS alone because its height is a token. A
 * page's own sticky bar is not: the /sounds filter bar and the /faq group
 * pills both wrap, so their height is measured. Published on <html> because
 * that is the scroller, and scroll-padding cannot read a variable declared on
 * a descendant. A hidden bar (display: none at this breakpoint) measures 0 and
 * publishes nothing.
 */
export function useStickyScrollReserve(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement

    const publish = () => {
      const height = el.getBoundingClientRect().height
      if (height === 0) {
        root.style.removeProperty(STICKY_BOTTOM_VAR)
        return
      }
      const top = parseFloat(getComputedStyle(el).top) || 0
      root.style.setProperty(STICKY_BOTTOM_VAR, `${Math.ceil(top + height)}px`)
    }

    publish()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish)
    observer?.observe(el)
    return () => {
      observer?.disconnect()
      root.style.removeProperty(STICKY_BOTTOM_VAR)
    }
  }, [ref])
}
